// Note analysis — follow-up detection, bucket grouping, and title extraction.
// Ported from the Jotly Notes Flow design prototype (jotly-notes.jsx).

const STOP_NAMES = new Set([
  'the', 'him', 'her', 'them', 'me', 'us', 'it', 'everyone', 'team', 'all',
  'my', 'his', 'our', 'your', 'someone', 'people', 'myself', 'that', 'this',
  'a', 'an', 'those',
]);

const ACTION_RE =
  /\b(email|e-mail|chase|call|ring|book|ask|send|pick|sort|finish|pay|draft|confirm|buy|fix|reply|follow ?up|follow-up|order|schedule|renew|cancel|remind|tell|check|review|share|update|prep|arrange|get back)\b/i;
const QUESTION_RE =
  /\?\s*$|\b(question mark|tbd|to be decided|do we|should we|what about|how do we|not sure (if|whether))\b/i;
const WAITING_RE =
  /\b(waiting on|waiting for|wait for|wait on|blocked on|blocked by|need .+ from|once .+ (sends?|replies|gets? back|confirms?)|until .+ (sends?|confirms?))\b/i;
const HEDGE_RE = /\b(maybe|might|possibly|perhaps|not sure|could|explore|idea)\b/i;

function stripMarks(l: string): string {
  return l
    .replace(/^#+\s*/, '')
    .replace(/^[-*]\s+\[[ xX/!]\]\s*/, '')
    .replace(/^[-*]\s+/, '')
    .replace(/^>\s+/, '')
    .trim();
}

function isFollowText(t: string): boolean {
  return ACTION_RE.test(t) || (/\blet\b/i.test(t) && /\bknow\b/i.test(t));
}

function personIn(text: string): string | null {
  const re =
    /\b(?:with|to|let|tell|ask|remind|chase|email|call|ring)\s+([A-Za-z][a-z]+)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const name = m[1];
    if (
      name[0] === name[0].toUpperCase() &&
      !STOP_NAMES.has(name.toLowerCase())
    ) {
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
  }
  return null;
}

export interface FollowUp {
  key: string;
  text: string;
  person: string | null;
  conf: 'sure' | 'maybe';
}

export function detectFollowups(
  body: string,
  committedOnly: boolean,
): FollowUp[] {
  const lines = (body || '').split('\n');
  const out: FollowUp[] = [];
  const seen = new Set<string>();
  let titleDone = false;
  const lastIdx = lines.length - 1;

  lines.forEach((raw, idx) => {
    if (!raw.trim()) return;
    if (committedOnly && idx === lastIdx) return;
    if (!titleDone) {
      titleDone = true;
      return;
    }
    if (/^#{1,}\s+/.test(raw)) return;
    const text = stripMarks(raw);
    if (!text || text.length < 3) return;
    if (QUESTION_RE.test(text) || WAITING_RE.test(text)) return;
    if (!isFollowText(text)) return;
    const key = text
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .slice(0, 80);
    if (seen.has(key)) return;
    seen.add(key);
    const conf =
      HEDGE_RE.test(text) || /\?\s*$/.test(raw) ? 'maybe' : 'sure';
    out.push({ key, text, person: personIn(text), conf });
  });

  return out;
}

export interface TodoItem {
  text: string;
  person: string | null;
  done: boolean;
}

export interface Buckets {
  todo: TodoItem[];
  questions: string[];
  waiting: string[];
  keypoints: string[];
}

export function groupBuckets(body: string): Buckets {
  const lines = (body || '').split('\n');
  const g: Buckets = { todo: [], questions: [], waiting: [], keypoints: [] };
  let titleDone = false;

  lines.forEach((raw) => {
    if (!raw.trim()) return;
    if (!titleDone) {
      titleDone = true;
      return;
    }
    if (/^#{1,}\s+/.test(raw)) return;
    const text = stripMarks(raw);
    if (!text) return;
    const done = /^[-*]\s+\[[xX]\]/.test(raw);
    if (WAITING_RE.test(text)) {
      g.waiting.push(text);
      return;
    }
    if (QUESTION_RE.test(text)) {
      g.questions.push(text);
      return;
    }
    if (done || isFollowText(text)) {
      g.todo.push({ text, person: personIn(text), done });
      return;
    }
    g.keypoints.push(text);
  });

  return g;
}

export function firstLineTitle(body: string): string {
  const l = (body || '')
    .split('\n')
    .find((x) => x.trim());
  return l
    ? l
        .replace(/^#+\s*/, '')
        .replace(/^[-*]\s+\[[ xX/!]\]\s*/, '')
        .trim()
    : '';
}

export function notePreview(body: string): string {
  return (body || '')
    .replace(/[#>*`]/g, '')
    .replace(/^[-\s]*\[[ xX/!]\]/gm, '·')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 92);
}

export function openTaskCount(body: string): number {
  return ((body || '').match(/\[( |\/|!)\]/g) || []).length;
}
